import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Flame, ArrowRight, Mic, BookOpen, Wand2, Sparkles } from "lucide-react";
import lessons from "../data/lessons.json";
import { progressAPI } from "../api";
import { isPremium } from "../components/PremiumGate";
import { getStats } from "../gamification";
import DailySchedule from "../components/DailySchedule";
import WordOfDay from "../components/WordOfDay";
import Card from "../components/atoms/Card";
import Chip from "../components/atoms/Chip";
import Button from "../components/atoms/Button";
import Mascot from "../components/atoms/Mascot";

const LEVEL_TONES = {
  Beginner:     { tone: "sage", spine: "var(--sage)" },
  Intermediate: { tone: "gold", spine: "var(--gold-500)" },
  Advanced:     { tone: "rust", spine: "var(--rust)" },
  IELTS:        { tone: "ink",  spine: "var(--ink-800)" },
};

function pickLevelTone(name = "") {
  return LEVEL_TONES[(name || "").split(" ")[0]] || LEVEL_TONES.Beginner;
}

function pickPennyPrompt(stats, day) {
  // Tiny rotation of coach prompts. In the Penny memory phase this becomes
  // personalized to weak areas.
  const prompts = [
    `Day ${day} is built on yesterday's pattern. Twenty minutes will surprise you.`,
    `If you can do one thing today, do the oral half. The written can wait.`,
    `${stats.streak >= 7 ? "A week in a row." : "Keep the streak alive."} Penny noticed.`,
    `Paste any message in Rephrase before the lesson. It warms the muscles.`,
  ];
  return prompts[(day + (stats.streak || 0)) % prompts.length];
}

export default function Home() {
  const [currentDay, setCurrentDay] = useState(() => Number(localStorage.getItem("currentDay") || 1));

  useEffect(() => {
    progressAPI.get().then((data) => {
      if (data.currentDay) {
        setCurrentDay(data.currentDay);
        localStorage.setItem("currentDay", String(data.currentDay));
      }
    }).catch(() => {});
  }, []);

  const todayLesson = lessons.find((l) => l.day === currentDay) || lessons[0];
  const stats = getStats();
  const user = (() => { try { return JSON.parse(localStorage.getItem("currentUser") || "{}"); } catch { return {}; } })();
  const firstName = user.name?.split(" ")[0] || "Learner";
  const tone = pickLevelTone(todayLesson.levelName || "Beginner");
  const today = new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });

  return (
    <div className="page-enter" style={{ display: "flex", flexDirection: "column", gap: 24, paddingBottom: 48 }}>

      {/* Compact greeting */}
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Mascot size={72} mood="happy" />
          <div>
            <p style={{ fontSize: 13, color: "var(--fg-3)", margin: 0, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 800 }}>
              {today}
            </p>
            <h1 className="font-display" style={{ fontSize: "clamp(34px, 5vw, 50px)", lineHeight: 1.02, fontWeight: 800, letterSpacing: "-0.01em", margin: "6px 0 0" }}>
              Hello again, <span style={{ color: "var(--feather-dark)" }}>{firstName}!</span>
            </h1>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Chip tone="rust" icon={<Flame size={13} strokeWidth={2.4} className="streak-fire" />}>{stats.streak || 0} day{stats.streak === 1 ? "" : "s"}</Chip>
          <Chip tone="gold">Day {currentDay} of {lessons.length}</Chip>
        </div>
      </header>

      {/* Hero — today's lesson, one dominant action */}
      <Card spineColor={tone.spine} style={{ paddingTop: 32, paddingBottom: 32 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <Chip tone={tone.tone}>{todayLesson.levelName || "Lesson"}</Chip>
          <Chip tone="neutral">Day {todayLesson.day}</Chip>
        </div>
        <h2 className="font-display" style={{ fontSize: "clamp(34px, 4.5vw, 48px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.02em", margin: "0 0 12px", maxWidth: "22ch" }}>
          {todayLesson.topic.split(/(\s+)/).map((seg, i) =>
            i === 1 ? <em key={i} style={{ color: "var(--gold-500)" }}>{seg}</em> : seg
          )}
        </h2>
        {todayLesson.sentence && (
          <p className="font-display" style={{ fontStyle: "italic", color: "var(--fg-2)", fontSize: 19, margin: "0 0 20px", lineHeight: 1.5, maxWidth: "40ch" }}>
            "{todayLesson.sentence}"
          </p>
        )}
        <p style={{ color: "var(--fg-3)", fontSize: 14, margin: "0 0 22px", fontStyle: "italic" }}>
          <Sparkles size={13} strokeWidth={1.8} style={{ display: "inline", verticalAlign: -2, marginRight: 6, color: "var(--gold-500)" }} />
          {pickPennyPrompt(stats, currentDay)}
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Link to={`/lesson/${todayLesson.day}`} style={{ textDecoration: "none" }}>
            <Button variant="primary" size="lg" iconRight={<ArrowRight size={15} strokeWidth={1.8} />}>
              Start written
            </Button>
          </Link>
          <Link to={`/oral/${todayLesson.day}`} style={{ textDecoration: "none" }}>
            <Button variant="ghost" size="lg" iconLeft={<Mic size={14} strokeWidth={1.8} />}>
              Oral half
            </Button>
          </Link>
        </div>
      </Card>

      {/* Today's 2-hour plan tracker */}
      <DailySchedule />

      {/* Two-column secondary: Rephrase quick + Word of Day */}
      <div className="grid" style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16 }}>
        <Link to="/write/rephrase" style={{ textDecoration: "none", display: "block" }}>
          <Card paperDeep style={{ height: "100%", borderTop: "3px solid var(--gold-500)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <Wand2 size={18} strokeWidth={1.6} color="var(--gold-500)" />
              <span className="eyebrow">Rephrase Studio</span>
            </div>
            <p className="font-display" style={{ fontSize: 24, lineHeight: 1.15, margin: "0 0 8px", fontWeight: 400 }}>
              Paste a message — <em style={{ color: "var(--gold-500)" }}>ten tones.</em>
            </p>
            <p style={{ color: "var(--fg-3)", fontSize: 14, margin: 0 }}>
              The fastest two minutes you'll spend on writing today.
            </p>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--gold-700)", fontSize: 13, fontWeight: 600, marginTop: 12 }}>
              Open <ArrowRight size={13} strokeWidth={2} />
            </div>
          </Card>
        </Link>
        <WordOfDay />
      </div>

      {/* Soft Premium nudge — only if not premium, not loud */}
      {!isPremium() && currentDay >= 8 && (
        <Link to="/pricing" style={{ textDecoration: "none" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "16px 20px", background: "var(--ink-900)", color: "var(--paper)", borderRadius: "var(--radius-md)" }}>
            <div>
              <p style={{ fontFamily: "var(--font-display)", fontSize: 20, margin: 0, fontWeight: 400 }}>
                <em style={{ color: "var(--gold-300)" }}>Day {currentDay}</em> is the last free lesson.
              </p>
              <p style={{ fontSize: 13, margin: "4px 0 0", color: "rgba(251,246,232,0.65)" }}>
                Premium opens the next 38 — ₹249/mo, cancel any time.
              </p>
            </div>
            <ArrowRight size={18} strokeWidth={1.8} color="var(--gold-300)" />
          </div>
        </Link>
      )}

      {/* Quick links to hubs */}
      <div style={{ display: "flex", gap: 12, justifyContent: "center", padding: "12px 0", flexWrap: "wrap" }}>
        <Link to="/speak" style={{ color: "var(--fg-3)", textDecoration: "none", fontSize: 13, fontWeight: 600, letterSpacing: "0.02em" }}>Speak →</Link>
        <span style={{ color: "var(--ink-200)" }}>·</span>
        <Link to="/practice" style={{ color: "var(--fg-3)", textDecoration: "none", fontSize: 13, fontWeight: 600, letterSpacing: "0.02em" }}>Practice →</Link>
        <span style={{ color: "var(--ink-200)" }}>·</span>
        <Link to="/career" style={{ color: "var(--fg-3)", textDecoration: "none", fontSize: 13, fontWeight: 600, letterSpacing: "0.02em" }}>Career →</Link>
        <span style={{ color: "var(--ink-200)" }}>·</span>
        <Link to="/penny" style={{ color: "var(--fg-3)", textDecoration: "none", fontSize: 13, fontWeight: 600, letterSpacing: "0.02em" }}>Progress →</Link>
      </div>
    </div>
  );
}
